import {
  AI_SPREADSHEET_CELL_KIND,
  isAiSpreadsheetNumericValueType,
} from "@/payroll/ai-script/types";
import { convertFormulaToR1C1 } from "./formula-reference-utils";
import type {
  WorkbookCopiedCell,
  WorkbookCopiedRange,
} from "./workbook-clipboard";

const GOOGLE_SHEETS_CELL_KIND = {
  formula: 203,
  value: 195,
} as const;

const GOOGLE_SHEETS_VALUE_TYPE = {
  number: 1,
  text: 2,
} as const;

const GOOGLE_SHEETS_CELL_VALUE_FIELD = {
  type: 1,
  text: 2,
  number: 3,
} as const;

const GOOGLE_SHEETS_CELL_VALUE_KIND = {
  number: 3,
  text: 2,
} as const;

const GOOGLE_SHEETS_VALUE_GRID_FIELD = {
  types: 1,
  numericValues: 3,
  textValues: 4,
} as const;

const GOOGLE_SHEETS_COMPACT_TABLE_FIELD = {
  version: 1,
  cellKinds: 2,
  values: 3,
  formats: 4,
  formatIndexes: 5,
  formulas: 8,
  formulaIndexes: 9,
  horizontalAlignments: 12,
  verticalAlignments: 13,
  dimensions: 15,
  cellCount: 20,
} as const;

const GOOGLE_SHEETS_DIMENSION_FIELD = {
  rows: 1,
  columns: 2,
} as const;

const GOOGLE_SHEETS_COMPACT_TABLE_VERSION = 1;
const GOOGLE_SHEETS_DEFAULT_FORMAT_INDEX = 0;
const GOOGLE_SHEETS_DEFAULT_ALIGNMENT = 1;

interface GoogleSheetsCellValue {
  numberValue?: number;
  textValue?: string;
  type:
    | (typeof GOOGLE_SHEETS_VALUE_TYPE)["number"]
    | (typeof GOOGLE_SHEETS_VALUE_TYPE)["text"];
}

const getGoogleSheetsCellValue = (
  displayValue: string,
  cell: WorkbookCopiedCell | null,
): GoogleSheetsCellValue => {
  const value = cell?.computedValue ?? displayValue;

  if (typeof value === "boolean") {
    return {
      numberValue: value ? 1 : 0,
      type: GOOGLE_SHEETS_VALUE_TYPE.number,
    };
  }

  const numericValue =
    typeof value === "number"
      ? value
      : String(value).trim() !== "" &&
          isAiSpreadsheetNumericValueType(cell?.valueType)
        ? Number(value)
        : Number.NaN;
  if (Number.isFinite(numericValue)) {
    return { numberValue: numericValue, type: GOOGLE_SHEETS_VALUE_TYPE.number };
  }

  return { textValue: String(value), type: GOOGLE_SHEETS_VALUE_TYPE.text };
};

export const buildGoogleSheetsValue = (
  displayValue: string,
  cell: WorkbookCopiedCell | null,
) => {
  const value = getGoogleSheetsCellValue(displayValue, cell);
  return JSON.stringify(
    value.type === GOOGLE_SHEETS_VALUE_TYPE.number
      ? {
          [GOOGLE_SHEETS_CELL_VALUE_FIELD.type]:
            GOOGLE_SHEETS_CELL_VALUE_KIND.number,
          [GOOGLE_SHEETS_CELL_VALUE_FIELD.number]: value.numberValue,
        }
      : {
          [GOOGLE_SHEETS_CELL_VALUE_FIELD.type]:
            GOOGLE_SHEETS_CELL_VALUE_KIND.text,
          [GOOGLE_SHEETS_CELL_VALUE_FIELD.text]: value.textValue,
        },
  );
};

const encodeGoogleSheetsRuns = (values: readonly number[]) => {
  const encoded: number[] = [];

  for (let index = 0; index < values.length; ) {
    const value = values[index];
    let runLength = 1;
    while (
      index + runLength < values.length &&
      values[index + runLength] === value
    ) {
      runLength += 1;
    }
    if (runLength > 1) encoded.push(-runLength);
    encoded.push(value);
    index += runLength;
  }

  return encoded;
};

// Google Sheets uses this private compact-table clipboard type—not its HTML
// table—to transfer formulas between sheets. The numeric keys mirror the
// payload Sheets itself writes: row-major cell kinds/values, formula indexes,
// and the copied rectangle dimensions. HTML remains the formatting fallback
// for other paste targets.
export const buildGoogleSheetsCompactTable = (
  copiedRange: WorkbookCopiedRange,
) => {
  const cellKinds: number[] = [];
  const cellTypes: number[] = [];
  const numericValues: number[] = [];
  const textValues: string[] = [];
  const formulas: string[] = [];
  const formulaIndexes: number[] = [];
  const formulaIndexByValue = new Map<string, number>();

  copiedRange.values.forEach((row, rowIndex) => {
    row.forEach((displayValue, colIndex) => {
      const cell = copiedRange.cells?.[rowIndex]?.[colIndex] ?? null;
      const value = getGoogleSheetsCellValue(displayValue, cell);
      const isFormula =
        cell?.kind === AI_SPREADSHEET_CELL_KIND.formula &&
        cell.rawInput.trimStart().startsWith("=");

      cellKinds.push(
        isFormula
          ? GOOGLE_SHEETS_CELL_KIND.formula
          : GOOGLE_SHEETS_CELL_KIND.value,
      );
      cellTypes.push(value.type);
      if (value.type === GOOGLE_SHEETS_VALUE_TYPE.number) {
        numericValues.push(value.numberValue ?? 0);
      } else textValues.push(value.textValue ?? "");

      if (isFormula) {
        const formula = convertFormulaToR1C1(
          cell.rawInput,
          cell.sourceRow ?? copiedRange.startRow + rowIndex,
          cell.sourceCol ?? copiedRange.startCol + colIndex,
        );
        let formulaIndex = formulaIndexByValue.get(formula);
        if (formulaIndex === undefined) {
          formulaIndex = formulas.length;
          formulas.push(formula);
          formulaIndexByValue.set(formula, formulaIndex);
        }
        formulaIndexes.push(formulaIndex);
      }
    });
  });

  const rowCount = copiedRange.values.length;
  const colCount = copiedRange.values[0]?.length ?? 0;
  const cellCount = rowCount * colCount;
  const values: Record<number, unknown> = {
    [GOOGLE_SHEETS_VALUE_GRID_FIELD.types]: cellTypes,
  };
  if (numericValues.length > 0) {
    values[GOOGLE_SHEETS_VALUE_GRID_FIELD.numericValues] = numericValues;
  }
  if (textValues.length > 0) {
    values[GOOGLE_SHEETS_VALUE_GRID_FIELD.textValues] = textValues;
  }

  const compactTable: Record<number, unknown> = {
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.version]:
      GOOGLE_SHEETS_COMPACT_TABLE_VERSION,
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.cellKinds]:
      encodeGoogleSheetsRuns(cellKinds),
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.values]: values,
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.formats]: [{}],
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.formatIndexes]: encodeGoogleSheetsRuns(
      Array(cellCount).fill(GOOGLE_SHEETS_DEFAULT_FORMAT_INDEX),
    ),
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.horizontalAlignments]:
      encodeGoogleSheetsRuns(
        Array(cellCount).fill(GOOGLE_SHEETS_DEFAULT_ALIGNMENT),
      ),
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.verticalAlignments]:
      encodeGoogleSheetsRuns(
        Array(cellCount).fill(GOOGLE_SHEETS_DEFAULT_ALIGNMENT),
      ),
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.dimensions]: {
      [GOOGLE_SHEETS_DIMENSION_FIELD.rows]: rowCount,
      [GOOGLE_SHEETS_DIMENSION_FIELD.columns]: colCount,
    },
    [GOOGLE_SHEETS_COMPACT_TABLE_FIELD.cellCount]: cellCount,
  };
  if (formulas.length > 0) {
    compactTable[GOOGLE_SHEETS_COMPACT_TABLE_FIELD.formulas] = formulas;
    compactTable[GOOGLE_SHEETS_COMPACT_TABLE_FIELD.formulaIndexes] =
      encodeGoogleSheetsRuns(formulaIndexes);
  }

  return JSON.stringify(compactTable);
};
